<template>
    <dialog-modal
        :primary-button-label="trans('general.action.apply')"
        :show="show"
        :teleport="true"
        max-width="lg"
        @close="$emit('close')"
        @submit="save"
    >
        <template #title>{{ title || trans('locations.attributes.modal.title') }}</template>

        <div v-if="(level === 2 || level === 3) && adjacentLineOptions.length" class="border-bottom mb-3 pb-3">
            <multi-select-dropdown
                :is-form-field="true"
                :label="trans('locations.adjacent_lanes.label')"
                :model-value="selectedLanes"
                :options="adjacentLineOptions"
                :placeholder="trans('locations.adjacent_lanes.placeholder')"
                @update:model-value="selectedLanes = $event"
            />
            <p class="small mt-1 mb-0 text-muted">{{ trans('locations.adjacent_lanes.help') }}</p>
        </div>

        <p v-if="rows.length === 0" class="text-muted">{{ trans('locations.attributes.empty') }}</p>

        <div v-for="(row, index) in rows" :key="index" class="border-bottom mb-3 pb-3">
            <div class="d-flex align-items-start" style="gap: 12px">
                <div class="flex-grow-1">
                    <label class="form-label small fw-bold">{{ trans('locations.attributes.key') }}</label>
                    <select :value="row.custom ? '__custom__' : row.key" class="form-control" @change="onKeyChange(row, $event.target.value)">
                        <option value="">{{ trans('locations.attributes.select_key') }}…</option>
                        <option v-for="definition in availableDefinitions(index)" :key="definition.key" :value="definition.key">
                            {{ definition.label }}
                        </option>
                        <option value="__custom__">{{ trans('locations.attributes.add_custom') }}…</option>
                    </select>
                    <input
                        v-if="row.custom"
                        v-model="row.key"
                        :placeholder="trans('locations.attributes.key_placeholder')"
                        class="form-control mt-2"
                        type="text"
                    />
                    <p v-else-if="definitionFor(row.key)?.description" class="small mt-1 mb-0 text-muted">
                        {{ definitionFor(row.key).description }}
                    </p>

                    <label class="form-label small fw-bold mt-2">{{ trans('locations.attributes.value') }}</label>
                    <select v-if="optionsFor(row.key)" v-model="row.value" class="form-control">
                        <option value="">—</option>
                        <option v-for="option in optionsFor(row.key)" :key="option" :value="option">{{ optionLabel(option) }}</option>
                    </select>
                    <input v-else v-model="row.value" :placeholder="trans('locations.attributes.value')" class="form-control" type="text" />
                </div>
                <button :aria-label="trans('locations.attributes.remove')" class="btn-icon mt-4 text-danger" type="button" @click="removeRow(index)">
                    <icon name="cil-x-circle" />
                </button>
            </div>
        </div>

        <jet-button class="mt-2" type="button" variant="tertiary" @click="addRow">+ {{ trans('locations.attributes.add') }}</jet-button>
    </dialog-modal>
</template>

<script setup>
import DialogModal from '@/Jetstream/DialogModal.vue';
import JetButton from '@/Jetstream/Button.vue';
import MultiSelectDropdown from '@/Components/MultiSelectDropdown.vue';
import { ref, watch } from 'vue';

const props = defineProps({
    show: { type: Boolean, default: false },
    modelValue: { type: Object, default: () => ({}) },
    definitions: { type: Array, default: () => [] },
    level: { type: Number, default: null },
    title: { type: String, default: '' },
    // Relationship-backed field (lanes this node serves), rendered for leaf nodes.
    // Options are { value, label }; the outer save action syncs them to the pivot.
    adjacentLineIds: { type: Array, default: () => [] },
    adjacentLineOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'save']);

const rows = ref([]);
const selectedLanes = ref([]);

const definitionFor = (key) => props.definitions.find((definition) => definition.key === key) || null;
const optionsFor = (key) => definitionFor(key)?.options || null;
// Options are stored as snake_case slugs; show a humanised label so raw
// identifiers (e.g. screening_lanes, private_search) aren't surfaced verbatim.
const optionLabel = (option) => option.charAt(0).toUpperCase() + option.slice(1).replace(/_/g, ' ');

// A predefined key applies to this node when it declares no levels, or includes this level.
const appliesToLevel = (definition) => !definition.levels?.length || props.level === null || definition.levels.includes(props.level);

// Predefined keys still selectable for a given row: applicable to this level and not already used by another row.
const availableDefinitions = (index) => {
    const usedElsewhere = rows.value.filter((_, rowIndex) => rowIndex !== index).map((row) => row.key);
    return props.definitions.filter((definition) => appliesToLevel(definition) && !usedElsewhere.includes(definition.key));
};

const buildRows = () => {
    rows.value = Object.entries(props.modelValue || {}).map(([key, value]) => ({
        key,
        value: value ?? '',
        custom: !definitionFor(key),
    }));
};

watch(
    () => props.show,
    (visible) => {
        if (visible) {
            buildRows();
            selectedLanes.value = [...(props.adjacentLineIds || [])];
        }
    },
    { immediate: true },
);

const onKeyChange = (row, value) => {
    if (value === '__custom__') {
        row.custom = true;
        row.key = '';
        row.value = '';
        return;
    }

    row.custom = false;
    row.key = value;

    // Drop a carried-over value that isn't valid for the newly selected key's options.
    const options = optionsFor(value);
    if (options && !options.includes(row.value)) {
        row.value = '';
    }
};

const addRow = () => {
    rows.value.push({ key: '', value: '', custom: false });
};

const removeRow = (index) => {
    rows.value.splice(index, 1);
};

const save = () => {
    const map = {};
    rows.value.forEach((row) => {
        const key = (row.key || '').trim();
        const value = (row.value ?? '').toString().trim();
        // A key only persists if it actually has a value; blank/whitespace-only is dropped.
        if (!key || value === '') {
            return;
        }
        map[key] = value;
    });
    emit('save', { managedAttributes: map, adjacentLineIds: selectedLanes.value });
};
</script>
